import * as React from "react";
import { connect } from "react-redux";
import { createFragmentContainer } from "react-relay";

// material-ui
import withStyles from "@material-ui/core/styles/withStyles";
import { Button } from "@material-ui/core";

// components
import { EventPersonalCalendarInfo } from "@components/event-personal-calendar-info";
import { EventSmartHoldInfo } from "@components/event-smart-hold-info";
import { EventSummary } from "@components/event-summary";
import { EventPeopleRenderer } from "@components/event-people";
import { CalloutBox } from "@components/callout-box";
import { H2 } from "@ui";
import { EventTeam } from "@components/event-team";

// util
import { ChromeSite, getChromeSite } from "@util/chrome-extension.util";
import {
  createEventParentRelayIdFromEvent,
  isEventSyncedFromPersonalCalendar,
  isLunchSmartHold,
  isFocusTimeSmartHold,
  isTravelTimeSmartHold,
} from "@util/event.util";
import { eventFromParent } from "@clockwise/client-commons/src/util/event";
import { getUserTagValueOffEvent } from "@util/event-tag.util";
import { getOrganizerFeatureStatesFromEvent } from "@util/feature-grid.util";
import { track, TrackingEvents } from "@util/analytics.util";
import { createUUID } from "@clockwise/client-commons/src/util/uuid";
import { PostMessageManager } from "@util/post-message.util";
import { isShadowCalendarId } from "@util/calendar-id.util";
import { logger } from "@util/logger.util";
import { roomString } from "@clockwise/client-commons/src/util/event";
import { Tags, IgnoreState } from "@clockwise/client-commons/src/util/event-tag";

// style
import spacing from "@clockwise/web-commons/src/styles/spacing.styles";

// state
import {
  makeEventReschedulerViewActive,
  updateSchedulingSessionId,
} from "@actions/feed-scheduler.actions";
import {
  unsetRecentlyRescheduled,
  setActiveViewAutopilotList,
} from "@actions/chrome-extension.actions";
import { IReduxState } from "@reducers/root.reducer";

// internal
import { eventFragments } from "./Event.gql";
import { IProps, IContainer, IState } from "./Event.d";
import * as s from "./Event.styles";
import { AutopilotEventHistory } from "@components/autopilot-event-history";
import { getValue } from "@src/util/errorable.util";
import { EventScheduling } from "@components/event-scheduling/EventScheduling";
import {
  EventColorCategory,
  getEventCategory,
} from "@clockwise/client-commons/src/util/event-category-coloring";

const MAX_ATTENDEES = 15;
export const eventSurpassesMaxAttendeesForSuggestedSlots = (event: {
  attendees: { __typename: "Attendee" }[];
}): boolean => {
  // Services does not compute suggested slots for events with more then 15 attendees
  // TODO (jimmy): Move this logic to webserver
  return event.attendees && event.attendees.length > MAX_ATTENDEES;
};

class EventComponent extends React.PureComponent<IProps, IState> {
  private recentlyRescheduledTimeout: ReturnType<typeof setTimeout>;

  constructor(props: IProps) {
    super(props);

    this.state = {
      showRescheduled: props.recentlyRescheduled,
    };
  }

  public componentDidMount() {
    const { eventParent, calendarId, recentlyRescheduled, canReschedule, user } = this.props;
    if (recentlyRescheduled) {
      this.props.unsetRecentlyRescheduled();
      this.recentlyRescheduledTimeout = setTimeout(() => {
        this.setState({ showRescheduled: false });
      }, 10000);
    }
    // fire post message so gcal ui is aware if currently open event can be rescheduled
    const event = eventFromParent(eventParent, calendarId);
    if (!event) {
      logger.error("Unable to find event to send active event to chrome extension");
      return;
    }
    const organizerFeatureStates = getOrganizerFeatureStatesFromEvent(event);

    const eventParentRelayId = createEventParentRelayIdFromEvent(event);

    const ignoreValue = getUserTagValueOffEvent(event, Tags.IgnoreState, user.id);
    let ignoreState: IgnoreState | undefined;
    if (ignoreValue) {
      ignoreState = IgnoreState[ignoreValue.value as keyof typeof IgnoreState];
    }

    PostMessageManager.sendActiveEvent(
      event.eventKey.externalEventId,
      calendarId,
      canReschedule,
      event,
      organizerFeatureStates,
      eventParentRelayId,
      ignoreState,
    );
  }

  public componentWillUnmount() {
    if (this.recentlyRescheduledTimeout) {
      clearTimeout(this.recentlyRescheduledTimeout);
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (nextProps.openReschedule && !this.props.openReschedule) {
      this.onClickReschedule(nextProps);
    }

    const { calendarId, canReschedule, user } = this.props;

    const event = eventFromParent(this.props.eventParent, calendarId);
    const nextEvent = eventFromParent(nextProps.eventParent, calendarId);

    if (nextEvent && nextEvent !== event) {
      const organizerFeatureStates = getOrganizerFeatureStatesFromEvent(nextEvent);

      const eventParentRelayId = (event && createEventParentRelayIdFromEvent(event)) ?? "";

      const ignoreValue = getUserTagValueOffEvent(nextEvent, Tags.IgnoreState, user.id);
      let ignoreState: IgnoreState | undefined;
      if (ignoreValue) {
        ignoreState = IgnoreState[ignoreValue.value as keyof typeof IgnoreState];
      }

      PostMessageManager.sendActiveEvent(
        nextEvent.eventKey.externalEventId,
        calendarId,
        canReschedule,
        nextEvent,
        organizerFeatureStates,
        eventParentRelayId,
        ignoreState,
      );
    }
  }

  // ~-~-~-~-~-~-~-
  // Helpers
  // ~-~-~-~-~-~-~-
  private onClickReschedule = (props: IProps) => {
    const { eventParent, calendarId } = props;
    const event = eventFromParent(eventParent, calendarId);
    if (!event) {
      logger.error("Unable to find event to reschedule");
      return;
    }

    // should this logic should be elsewhere?
    const schedulingSessionId = createUUID();
    this.props.updateSchedulingSessionId(schedulingSessionId);
    track(TrackingEvents.SCHEDULING.RESCHEDULER_STARTED, {
      schedulingSessionId,
      externalEventId: event.eventKey.externalEventId,
    });

    this.props.makeEventReschedulerViewActive(event);
  };

  private closeEventRescheduledConfirmation = () => {
    this.setState({
      showRescheduled: false,
    });
  };

  private onClickAutopilotListView = () => {
    this.props.setActiveViewAutopilotList();
  };

  private goToEditEvent = (
    scheduledExternalEventId: string,
    scheduledCalendarId: string,
    fromRescheduler = true,
  ) => {
    if (fromRescheduler) {
      this.closeEventRescheduledConfirmation();
      track(TrackingEvents.SCHEDULING.RESCHEDULER_RESCHEDULED_CONFIRMATION_EDIT_EVENT_CLICKED, {
        schedulingSessionId: this.props.schedulingSessionId,
        externalEventId: scheduledExternalEventId,
      }); // analytics
    }
    PostMessageManager.goToEventEditPage(scheduledExternalEventId, scheduledCalendarId);
  };

  // ~-~-~-~-~-~-~-
  // Render
  // ~-~-~-~-~-~-~-
  private maybeRenderAutopilotEventHistory = () => {
    const { org, eventParent, calendarId, showClockwiseHistory } = this.props;
    const event = eventFromParent(eventParent, calendarId);

    if (!event) {
      return null;
    }

    const orgAccountingPeriod = getValue(org?.orgAccountingPeriod);

    // Billing Group of Current User
    let currentUserBillingGroup = null;
    const featureGrid = getValue(org?.featureGrid);
    if (featureGrid) {
      currentUserBillingGroup =
        featureGrid.stateMetadata?.primaryBillingGroupMetadata?.billingGroup;
    }

    const organizerFromEvent = getValue(event?.organizerOrgPerson);

    return (
      <AutopilotEventHistory
        event={event}
        scrollIntoView={showClockwiseHistory}
        orgId={org?.id}
        orgAccountingPeriod={orgAccountingPeriod}
        calendarId={calendarId}
        organizerPersonId={organizerFromEvent?.personId || null}
        currentUserBillingGroup={currentUserBillingGroup}
        onViewAutopilotMeetings={this.onClickAutopilotListView}
      />
    );
  };

  public render() {
    const {
      user,
      org,
      relay,
      eventParent,
      calendarId,
      isChromeExtension,
      chromeExtensionOpen,
      canReschedule,
      canAutopilot,
      attendeeNotInDomain,
      organizerNotClockwiseUser,
      isAllDayEvent,
      smartHoldEventInfo,
      classes,
      withBorder,
    } = this.props;
    const event = eventFromParent(eventParent, calendarId);

    // no event — abort!
    if (!event) {
      return (
        <div style={{ ...s.section, paddingTop: 0 }}>
          <CalloutBox error>
            Unable to find this event, it may have been deleted. If this is a brand new event,
            please try again.
          </CalloutBox>
        </div>
      );
    }

    // shadow calendar / smart holds
    const shadowCalendarId = getValue(org.shadowCalendarSettingsErrorable)?.shadowCalendarId;
    const isShadowCalendar = !!shadowCalendarId && isShadowCalendarId(calendarId, shadowCalendarId);
    const isFocusTime = isFocusTimeSmartHold(event);
    const isLunch = isLunchSmartHold(event);
    const isTravelTime = isTravelTimeSmartHold(event);
    const eventCategory = getEventCategory(event, user.id);

    // determine rescheduling
    let enableRescheduling = canReschedule;
    let disableReschedulingReason = null;
    if (!enableRescheduling) {
      disableReschedulingReason = (
        <span>
          Sorry, you do not have
          <br />
          permission to edit this event.
        </span>
      );
    } else if (eventSurpassesMaxAttendeesForSuggestedSlots(event)) {
      enableRescheduling = false;
      disableReschedulingReason = (
        <span>
          Sorry, Clockwise can only currently
          <br />
          reschedule meetings with 15 or less people.
        </span>
      );
    }

    const hasAttendees = !!event.attendees.filter(
      (attendee) => attendee.urnValue.indexOf(roomString) === -1,
    ).length;

    // personal event
    const isSyncedFromPersonalCalendar = isEventSyncedFromPersonalCalendar(eventParent);
    const isOrganizer =
      getValue(event.organizerOrgPerson)?.isYou || getValue(event.organizerPerson)?.isYou || false;

    // private events are locked
    // event summary handles appropriate rendering
    if (event.locked) {
      return (
        <div className={classes[withBorder ? "containerBordered" : "chromeContainer"]}>
          <div style={{ ...s.section, paddingTop: 0 }}>
            <EventSummary
              event={event}
              canAutopilot={canAutopilot}
              org={org}
              user={user}
              calendarId={calendarId}
              open
              smartHoldEventInfo={null}
              eventParent={eventParent}
              attendeeNotInDomain={attendeeNotInDomain}
              organizerNotClockwiseUser={organizerNotClockwiseUser}
              isAllDayEvent={isAllDayEvent}
              canReschedule={canReschedule}
            />
          </div>
        </div>
      );
    }

    if (isSyncedFromPersonalCalendar) {
      const syncedOriginEvent = getValue(eventParent.syncedOriginEventErrorable);

      return (
        <div className={classes[withBorder ? "containerBordered" : "chromeContainer"]}>
          <div style={{ ...s.section, paddingTop: 0 }}>
            {syncedOriginEvent && (
              <EventSummary
                event={event}
                originEvent={syncedOriginEvent}
                org={org}
                user={user}
                calendarId={calendarId}
                hideColorSettings={!isOrganizer}
                canReschedule={canReschedule}
                smartHoldEventInfo={null}
                open
              />
            )}
            <EventPersonalCalendarInfo eventParent={eventParent} isOrganizer={isOrganizer} />
          </div>
          <EventTeam
            user={user}
            org={org}
            eventParent={eventParent}
            smartHoldEventInfo={smartHoldEventInfo}
            calendarId={calendarId}
          />
        </div>
      );
    }

    if (isShadowCalendar || isFocusTime || isLunch || isTravelTime) {
      return (
        <div className={classes[withBorder ? "containerBordered" : "chromeContainer"]}>
          <div style={{ ...s.section, paddingTop: 0 }}>
            <EventSummary
              event={event}
              org={org}
              user={user}
              calendarId={calendarId}
              smartHoldEventInfo={smartHoldEventInfo}
              canReschedule={canReschedule}
              open
            />
            <EventSmartHoldInfo
              event={event}
              org={org}
              user={user}
              calendarId={calendarId}
              smartHoldEventInfo={smartHoldEventInfo}
            />
          </div>
        </div>
      );
    }

    if (event.scrubbedEvent) {
      return (
        <div className={classes[withBorder ? "containerBordered" : "chromeContainer"]}>
          <div style={{ ...s.section, paddingTop: 0 }}>
            <EventSummary
              event={event}
              canAutopilot={canAutopilot}
              org={org}
              user={user}
              smartHoldEventInfo={null}
              calendarId={calendarId}
              canReschedule={canReschedule}
              open
            />
            {isChromeExtension && chromeExtensionOpen && getChromeSite() !== ChromeSite.Gcal ? (
              <Button
                style={{ marginTop: spacing.sm }}
                onClick={() => this.goToEditEvent(event.eventKey.externalEventId, calendarId)}
                variant="outlined"
                fullWidth
              >
                Edit in calendar
              </Button>
            ) : null}
          </div>
          <EventTeam
            user={this.props.user}
            org={org}
            eventParent={eventParent}
            smartHoldEventInfo={smartHoldEventInfo}
            calendarId={calendarId}
          />
          {chromeExtensionOpen && hasAttendees ? (
            <React.Fragment>
              <div style={s.divider} />
              <div style={s.section}>
                <H2>People</H2>
                <EventPeopleRenderer
                  orgId={org.id}
                  event={event}
                  externalEventId={eventParent.externalEventId}
                  eventCalendarId={calendarId}
                  environment={relay.environment as any}
                />
              </div>
            </React.Fragment>
          ) : null}
        </div>
      );
    }

    // all day event
    if (event.isAllDay) {
      return (
        <div className={classes[withBorder ? "containerBordered" : "chromeContainer"]}>
          <div style={{ ...s.section, paddingTop: 0 }}>
            <EventSummary
              event={event}
              org={org}
              user={user}
              calendarId={calendarId}
              canReschedule={canReschedule}
              smartHoldEventInfo={null}
              open
            />
          </div>
          <EventTeam
            user={this.props.user}
            org={org}
            eventParent={eventParent}
            smartHoldEventInfo={smartHoldEventInfo}
            calendarId={calendarId}
          />
        </div>
      );
    }

    if (eventCategory === EventColorCategory.OutOfOffice) {
      return (
        <div className={withBorder ? classes.containerBordered : classes.chromeContainer}>
          <div style={{ ...s.section, paddingTop: 0 }}>
            <EventSummary
              event={event}
              canAutopilot={canAutopilot}
              org={org}
              user={user}
              calendarId={calendarId}
              open
              eventParent={eventParent}
              attendeeNotInDomain={attendeeNotInDomain}
              organizerNotClockwiseUser={organizerNotClockwiseUser}
              isAllDayEvent={isAllDayEvent}
              canReschedule={canReschedule}
              smartHoldEventInfo={null}
            />
          </div>
          <EventTeam
            user={this.props.user}
            org={org}
            eventParent={eventParent}
            smartHoldEventInfo={smartHoldEventInfo}
            calendarId={calendarId}
          />
        </div>
      );
    }

    // event we can actually render
    return (
      <div className={classes[withBorder ? "containerBordered" : "chromeContainer"]}>
        <div style={{ ...s.section, paddingTop: 0 }}>
          <EventSummary
            event={event}
            canAutopilot={canAutopilot}
            org={org}
            user={user}
            calendarId={calendarId}
            open
            eventParent={eventParent}
            attendeeNotInDomain={attendeeNotInDomain}
            organizerNotClockwiseUser={organizerNotClockwiseUser}
            smartHoldEventInfo={null}
            isAllDayEvent={isAllDayEvent}
            canReschedule={canReschedule}
          />
          {isChromeExtension && chromeExtensionOpen && getChromeSite() !== ChromeSite.Gcal ? (
            <Button
              style={{ marginTop: spacing.sm }}
              onClick={() => this.goToEditEvent(event.eventKey.externalEventId, calendarId)}
              variant="outlined"
              fullWidth
            >
              Edit in calendar
            </Button>
          ) : null}
        </div>
        <EventTeam
          user={this.props.user}
          org={org}
          eventParent={eventParent}
          smartHoldEventInfo={smartHoldEventInfo}
          calendarId={calendarId}
        />
        {chromeExtensionOpen && (
          <React.Fragment>
            <div style={s.divider} />
            <div style={s.section}>
              <EventScheduling
                orgId={org.id}
                externalEventId={eventParent.externalEventId}
                eventCalendarId={calendarId}
                enableRescheduling={enableRescheduling}
                disableReschedulingReason={disableReschedulingReason}
              />
            </div>
          </React.Fragment>
        )}
        <div style={s.divider} />
        {this.maybeRenderAutopilotEventHistory()}
      </div>
    );
  }
}

/////////////////
// With Styles //
/////////////////

export const EventStyled = withStyles(s.styles)(EventComponent);

///////////
// Redux //
///////////

function mapStateToProps(state: IReduxState, _ownProps: IContainer) {
  return {
    editEvent: state.webExtension.editEvent,
    chromeExtensionOpen: state.webExtension.webExtensionOpen,
    isChromeExtension: state.webExtension.isWebExtension,
    recentlyRescheduled: state.webExtension.activeViewEvent.recentlyRescheduled,
    openReschedule: state.webExtension.activeViewEvent.openReschedule,
    schedulingSessionId: state.feedScheduler.schedulingSessionId,
    showClockwiseHistory: state.webExtension.activeViewEvent.showClockwiseHistory,
  };
}

const reduxContainer = connect(mapStateToProps, {
  makeEventReschedulerViewActive,
  updateSchedulingSessionId,
  unsetRecentlyRescheduled,
  setActiveViewAutopilotList,
})(EventStyled);

///////////
// Relay //
///////////

export const Event = createFragmentContainer<IContainer>(reduxContainer, eventFragments);
